import React, { useContext, useEffect, useMemo, useState } from 'react'
import { NameDto } from '@/api/Common/Types'
import { ReactDatePicker } from '@/components/DatePicker/DatePicker'
import { Link, LinkProps } from '@/components/Link/Link'
import { MultiSelect, MultiSelectProps, Select, StringTagsMultiSelect } from '@/components/Select'
import type {
  SelectMenuItem,
  SelectOptionGroupInput,
  SelectOptionInput,
  SelectValue,
} from '@/components/Select/Select'
import { successToast, ToastContext } from '@/components/Toast/Toast'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { Checkbox } from '@/components/ui/checkbox'
import { Field, FieldError, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group'
import {
  Select as ReadonlySelect,
  SelectTrigger as ReadonlySelectTrigger,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { useResolvedDataLayout } from '@/utils/DataLayoutContext'
import { toUploadFileInfo } from '@/utils/file.helpers'
import { rawDateString, zonedTimeToUtc } from '@/utils/time-zone/helpers'
import type { UploadFileInfo } from '@progress/kendo-react-upload'
import copy from 'copy-to-clipboard'
import { FormikContext, FormikContextType, getIn } from 'formik'
import { Copy, ExternalLinkIcon, XIcon } from 'lucide-react'

type FormValues = Record<string, unknown>

export type FormFieldAction = {
  icon: React.ReactNode
  onClick: NonNullable<React.ComponentProps<typeof Button>['onClick']>
  ariaLabel?: string
  className?: string
  dataTest?: string
  disabled?: boolean
  key?: React.Key
  label?: string
  title?: string
  variant?: React.ComponentProps<typeof Button>['variant']
}

interface CommonProps {
  name?: string
  label: React.ReactNode
  className?: string
  actions?: FormFieldAction[]
  canCopy?: boolean
  rightIcon?: React.ReactNode
  rightAddon?: React.ReactNode
  link?: string | LinkProps
  error?: string | string[]
  required?: boolean
}

interface BaseFieldProps extends CommonProps {
  readOnly?: boolean
  disabled?: boolean
  dataTest?: string
}

interface FormFieldLayoutProps extends CommonProps {
  children: React.ReactNode
  touched?: boolean
  onBlur?: () => void
  value?: unknown
  isLinkedValueInteractive?: boolean
}

const getNestedValue = (obj: Record<string, unknown> | undefined, path?: string): unknown =>
  path ? getIn(obj, path) : undefined

const getValueKey = (path?: string) => path?.split('.')[0] ?? ''

const normalizeError = (
  error?: string | string[] | Record<string, unknown> | unknown
): string[] => {
  if (!error) return []
  if (Array.isArray(error)) return error.filter(Boolean) as string[]
  if (typeof error === 'string') return [error]

  if (typeof error === 'object') {
    return Object.values(error as Record<string, unknown>).filter(
      (v): v is string => typeof v === 'string' && Boolean(v)
    )
  }

  return []
}
const hasDisplayValue = (value?: unknown) => value != null && value !== ''

const hasRenderableValue = (value: React.ReactNode) =>
  value != null && value !== false && value !== ''

const getNodeText = (node: React.ReactNode): string => {
  if (node == null || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(getNodeText).join(' ')
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return getNodeText(node.props.children)
  }
  return ''
}

const useOptionalFormikContext = <T extends FormValues>(): FormikContextType<T> | null => {
  return useContext(FormikContext as React.Context<FormikContextType<T> | undefined>) ?? null
}

const validateRequiredField = (value: unknown): string | undefined => {
  if (value == null) return 'Required'
  if (typeof value === 'string' && value.trim() === '') return 'Required'
  if (Array.isArray(value) && value.length === 0) return 'Required'

  return undefined
}

const useFieldMeta = ({
  name,
  error,
  required,
}: {
  name?: string
  error?: string | string[]
  required?: boolean
}) => {
  const formik = useOptionalFormikContext<FormValues>()
  const [localTouched, setLocalTouched] = useState(false)
  const registerField = formik?.registerField
  const unregisterField = formik?.unregisterField

  useEffect(() => {
    if (!name || !required || !registerField || !unregisterField) return

    registerField(name, { validate: validateRequiredField })

    return () => unregisterField(name)
  }, [name, registerField, required, unregisterField])

  const formikTouched = name
    ? Boolean(getNestedValue(formik?.touched as FormValues | undefined, name))
    : false
  const formikError = name
    ? getNestedValue(formik?.errors as FormValues | undefined, name)
    : undefined

  const resolvedError = formikTouched
    ? normalizeError((formikError as string | string[] | undefined) ?? error)
    : normalizeError(error)

  const touchedState = formik ? formikTouched : localTouched

  const handleBlur = () => {
    if (!name) return

    if (formik) {
      formik.setFieldTouched(name, true)
      return
    }

    setLocalTouched(true)
  }

  return {
    formik,
    touched: touchedState,
    error: resolvedError,
    onBlur: handleBlur,
  }
}

const useFieldValue = <T,>({
  name,
  value,
  fallback,
}: {
  name?: string
  value?: T
  fallback?: T
}): T | undefined => {
  const formik = useOptionalFormikContext<FormValues>()
  const formikValue = name ? (getNestedValue(formik?.values, name) as T | undefined) : undefined

  if (value !== undefined) return value
  if (formikValue !== undefined) return formikValue
  return fallback
}

const useFieldSetter = () => {
  const formik = useOptionalFormikContext<FormValues>()

  return (name: string | undefined, value: unknown) => {
    if (!formik || !name) return
    formik.setFieldValue(name, value)
  }
}

const useDateFieldSetter = () => {
  const formik = useOptionalFormikContext<FormValues>()

  return {
    setFieldValue: (name: string | undefined, value: unknown) => {
      if (!formik || !name) return
      formik.setFieldValue(name, value)
    },
    initialValues: formik?.initialValues ?? {},
  }
}

const getLinkData = (link?: CommonProps['link']): LinkProps | null => {
  if (!link) return null
  const linkData = typeof link === 'object' ? link : { to: link }
  const target = linkData.target ?? '_blank'

  return {
    ...linkData,
    target,
    rel: target === '_blank' ? (linkData.rel ?? 'noopener noreferrer') : linkData.rel,
  }
}

const FieldActionGroup: React.FC<{
  value?: unknown
  actions?: CommonProps['actions']
  canCopy?: CommonProps['canCopy']
  rightIcon?: CommonProps['rightIcon']
  rightAddon?: CommonProps['rightAddon']
  link?: CommonProps['link']
  showLinkAction?: boolean
}> = ({ value, link, actions, canCopy, rightIcon, rightAddon, showLinkAction = true }) => {
  const setToast = useContext(ToastContext)
  const linkData = useMemo(() => getLinkData(link), [link])

  return (
    <>
      {canCopy && value && (
        <Button
          className='relative z-20'
          size='icon-xs'
          variant='outline'
          onClick={() => {
            copy(value.toString())
            setToast(successToast('Text copied to clipboard.'))
          }}
        >
          <Copy />
        </Button>
      )}

      {actions?.map((action, index) => (
        <Button
          key={action.key ?? action.label ?? index}
          aria-label={action.ariaLabel ?? action.label ?? action.title}
          className={cn('relative z-20', action.className)}
          data-test={action.dataTest}
          disabled={action.disabled}
          size='icon-xs'
          title={action.title ?? action.label}
          variant={action.variant ?? 'outline'}
          onClick={action.onClick}
        >
          {action.icon}
        </Button>
      ))}

      {rightIcon && (
        <Button className='pointer-events-none relative z-20' variant='outline' size='icon-xs'>
          {rightIcon}
        </Button>
      )}

      {showLinkAction && linkData && (
        <Button className='relative z-20' variant='outline' size='icon-xs'>
          <Link {...linkData} className='flex h-full w-full items-center justify-center'>
            <ExternalLinkIcon />
          </Link>
        </Button>
      )}

      {rightAddon && <span className='relative z-20 flex items-stretch'>{rightAddon}</span>}
    </>
  )
}

export const FormFieldLayout: React.FC<FormFieldLayoutProps> = ({
  name,
  error,
  label,
  className,
  value,
  children,
  actions,
  canCopy,
  rightIcon,
  rightAddon,
  link,
  touched,
  onBlur,
  isLinkedValueInteractive,
  required,
}) => {
  const { activeType } = useResolvedDataLayout()
  const isList = activeType === 'list'
  const normalizedError = normalizeError(error)
  const shouldShowErrors = touched ?? normalizedError.length > 0
  const linkData = useMemo(() => getLinkData(link), [link])
  const showLinkedValueAsText = Boolean(
    isLinkedValueInteractive && linkData && hasDisplayValue(value)
  )
  const linkLabel = typeof label === 'string' ? label : 'field'

  return (
    <Field
      orientation={isList ? 'horizontal' : 'vertical'}
      className={cn(
        'w-full',
        className,
        isList && 'grid grid-cols-[1fr_3fr] items-center gap-x-4 gap-y-1'
      )}
      onBlur={onBlur}
    >
      <FieldLabel htmlFor={name} className={cn(isList && 'shrink-0')}>
        {label}
        {required && <span>*</span>}
      </FieldLabel>

      <div className='min-w-0 flex-1'>
        <ButtonGroup
          className='relative w-full'
          data-linked-value={showLinkedValueAsText ? 'true' : undefined}
        >
          {showLinkedValueAsText && linkData ? (
            <Link
              {...linkData}
              aria-label={`Open ${linkLabel}`}
              className='inline-flex min-w-0 flex-1 items-center gap-1 py-1 text-sm text-foreground underline-offset-3 hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 [&_svg]:shrink-0'
            >
              <span className='truncate'>{value?.toString()}</span>
              <ExternalLinkIcon size={12} />
            </Link>
          ) : (
            children
          )}
          <FieldActionGroup
            value={value}
            actions={actions}
            canCopy={canCopy}
            rightIcon={rightIcon}
            rightAddon={rightAddon}
            link={link}
            showLinkAction={!showLinkedValueAsText}
          />
        </ButtonGroup>
      </div>

      {shouldShowErrors &&
        normalizedError.map((err, i) => (
          <FieldError key={i} className={cn(isList && 'col-start-2')}>
            {err}
          </FieldError>
        ))}
    </Field>
  )
}

type CommonControlProps = BaseFieldProps & {
  value?: unknown
}

const useCommonFieldLayoutProps = (props: CommonControlProps, displayValue?: string) => {
  const {
    name,
    label,
    className,
    error,
    actions,
    canCopy,
    rightIcon,
    rightAddon,
    link,
    readOnly,
    disabled,
    required,
  } = props
  const meta = useFieldMeta({ name, error, required })

  return {
    name,
    label,
    actions,
    canCopy,
    className,
    rightIcon,
    rightAddon,
    link,
    required,
    value: displayValue,
    error: meta.error,
    touched: meta.touched,
    onBlur: meta.onBlur,
    isLinkedValueInteractive: Boolean(link && (readOnly || disabled)),
  }
}

export const FormErrorText: React.FC<{ message?: React.ReactNode }> = ({ message }) =>
  message ? <div className='text-sm text-destructive'>{message}</div> : null

interface FormFieldBlockProps {
  children: React.ReactNode
  error?: React.ReactNode
  className?: string
}

export const FormFieldBlock: React.FC<FormFieldBlockProps> = ({ children, error, className }) => (
  <div className={cn('space-y-1', className)}>
    {children}
    <FormErrorText message={error} />
  </div>
)

interface FormFieldProps extends BaseFieldProps {
  onChange?: (value: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  onClick?: React.MouseEventHandler<HTMLInputElement>
  className?: string
  max?: React.ComponentProps<typeof Input>['max']
  min?: React.ComponentProps<typeof Input>['min']
  placeholder?: string
  step?: React.ComponentProps<typeof Input>['step']
  type?: React.ComponentProps<typeof Input>['type']
  value?: string | number
}

export const FormField: React.FC<FormFieldProps> = (props) => {
  const {
    name,
    value,
    disabled,
    onChange,
    onBlur,
    onClick,
    readOnly,
    className,
    max,
    min,
    placeholder,
    step,
    type = 'text',
    dataTest,
  } = props

  const setFieldValue = useFieldSetter()
  const resolvedValue =
    useFieldValue<string | number | undefined>({ name, value, fallback: '' }) ?? ''
  const layoutProps = useCommonFieldLayoutProps(props, resolvedValue?.toString())
  const isInteractiveReadonly = Boolean(readOnly && onClick && !disabled)

  return (
    <FormFieldLayout {...layoutProps}>
      <Input
        id={name}
        value={resolvedValue}
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholder}
        className={cn(className, isInteractiveReadonly && 'cursor-pointer')}
        type={type}
        min={min}
        max={max}
        step={step}
        data-test={dataTest}
        data-interactive-readonly={isInteractiveReadonly ? 'true' : undefined}
        onBlur={(event) => {
          layoutProps.onBlur?.()
          onBlur?.(event)
        }}
        onClick={onClick}
        onChange={(event) => {
          if (readOnly || disabled) return

          if (onChange) {
            onChange(event)
            return
          }

          setFieldValue(name, event.target.value)
        }}
      />
    </FormFieldLayout>
  )
}

interface FormReadonlyFieldProps extends BaseFieldProps {
  value?: React.ComponentProps<typeof Input>['value'] | null
  placeholder?: React.ComponentProps<typeof Input>['placeholder']
  className?: string
  readOnly?: boolean
}

export const FormReadonlyField: React.FC<FormReadonlyFieldProps> = (props) => {
  const { value, dataTest, placeholder, className, readOnly = true } = props
  const layoutProps = useCommonFieldLayoutProps({ ...props, readOnly }, value?.toString())
  const hasActionAddon = Boolean(props.rightAddon || props.actions?.length)

  return (
    <FormFieldLayout {...layoutProps}>
      <Input
        data-test={dataTest}
        readOnly={readOnly}
        value={value ?? ''}
        placeholder={placeholder}
        className={className}
        data-interactive-readonly={hasActionAddon ? 'true' : undefined}
      />
    </FormFieldLayout>
  )
}

interface FormTextareaFieldProps extends BaseFieldProps {
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>
  onClick?: React.MouseEventHandler<HTMLTextAreaElement>
  onChange?: (value: React.ChangeEvent<HTMLTextAreaElement>) => void
  className?: string
  placeholder?: string
  rows?: number
  maxLength?: number
  value?: string
}

export const FormTextareaField: React.FC<FormTextareaFieldProps> = (props) => {
  const {
    name,
    value,
    disabled,
    onChange,
    readOnly,
    className,
    placeholder,
    rows = 4,
    onBlur,
    onClick,
    maxLength,
    dataTest,
  } = props

  const setFieldValue = useFieldSetter()
  const resolvedValue = useFieldValue<string | undefined>({ name, value, fallback: '' }) ?? ''
  const layoutProps = useCommonFieldLayoutProps(props, resolvedValue)

  return (
    <FormFieldLayout {...layoutProps}>
      <Textarea
        id={name}
        value={resolvedValue}
        disabled={disabled}
        readOnly={readOnly}
        rows={rows}
        maxLength={maxLength}
        placeholder={placeholder}
        className={className}
        data-test={dataTest}
        onBlur={(event) => {
          layoutProps.onBlur?.()
          onBlur?.(event)
        }}
        onClick={onClick}
        onChange={(event) => {
          if (readOnly || disabled) return

          if (onChange) {
            onChange(event)
            return
          }

          setFieldValue(name, event.target.value)
        }}
      />
    </FormFieldLayout>
  )
}

interface FormStringTagsFieldProps extends BaseFieldProps {
  onChange?: (value: string[]) => void
  placeholder?: string
  value?: string[] | null
}

export const FormStringTagsField: React.FC<FormStringTagsFieldProps> = (props) => {
  const { name, value, placeholder, disabled, readOnly, dataTest, onChange } = props
  const setFieldValue = useFieldSetter()
  const resolvedValue = useFieldValue<string[] | null | undefined>({ name, value }) ?? null
  const layoutProps = useCommonFieldLayoutProps(props, resolvedValue?.join(', '))

  return (
    <FormFieldLayout {...layoutProps}>
      <div data-test={dataTest} className='w-full'>
        <StringTagsMultiSelect
          value={resolvedValue}
          placeholder={placeholder}
          disabled={readOnly || disabled}
          fullWidth
          onChange={(nextValue) => {
            if (readOnly || disabled) return

            if (onChange) {
              onChange(nextValue)
              return
            }

            setFieldValue(name, nextValue)
          }}
        />
      </div>
    </FormFieldLayout>
  )
}

interface CheckboxFieldProps extends BaseFieldProps {
  onChange?: (value: boolean) => void
  value?: boolean
}

export const FormCheckboxField: React.FC<CheckboxFieldProps> = (props) => {
  const { activeType } = useResolvedDataLayout()
  const {
    name,
    value,
    dataTest,
    actions,
    rightIcon,
    label,
    disabled,
    onChange,
    readOnly,
    error,
    className,
    required,
  } = props
  const setFieldValue = useFieldSetter()
  const meta = useFieldMeta({ name, error, required })
  const checked = Boolean(useFieldValue<boolean | undefined>({ name, value, fallback: false }))
  const hasActions = Boolean(actions?.length)

  return (
    <Field
      orientation='horizontal'
      className={cn('w-full', activeType === 'list' && 'items-center gap-3', className)}
    >
      <Checkbox
        id={name}
        name={name}
        disabled={disabled}
        readOnly={readOnly}
        data-test={dataTest}
        checked={checked}
        onBlur={() => meta.onBlur()}
        onCheckedChange={(nextChecked) => {
          if (readOnly || disabled) return

          if (onChange) {
            onChange(Boolean(nextChecked))
            return
          }

          setFieldValue(name, Boolean(nextChecked))
        }}
      />
      <div>
        <div className='flex min-w-0 items-center gap-2'>
          <FieldLabel htmlFor={name}>
            {label}
            {required && <span>*</span>}
            {rightIcon}
          </FieldLabel>
          {hasActions && (
            <ButtonGroup className='relative'>
              <FieldActionGroup actions={actions} showLinkAction={false} />
            </ButtonGroup>
          )}
        </div>
        {meta.touched && meta.error.map((err, i) => <FieldError key={i}>{err}</FieldError>)}
      </div>
    </Field>
  )
}

type PrimitiveOption = NameDto<number | string> & {
  rightAddon?: React.ReactNode
  disabled?: boolean
  text?: string
}

export type FormSelectOption = PrimitiveOption
export type FormSelectOptionGroup = {
  label: React.ReactNode
  options: PrimitiveOption[]
}

type FormSelectOptions = Array<FormSelectOption | FormSelectOptionGroup>
type PrimitiveValue = string | number

const isPrimitiveValue = (value: unknown): value is PrimitiveValue =>
  (typeof value === 'string' && value !== '') || typeof value === 'number'

const isGroupedOption = (
  option: FormSelectOption | FormSelectOptionGroup
): option is FormSelectOptionGroup => 'options' in option

const getNormalizedSelectValue = (value: unknown): SelectValue | undefined => {
  if (value == null || typeof value === 'boolean') return undefined

  if (Array.isArray(value)) {
    return value
      .map(getNormalizedSelectValue)
      .flatMap((item) => (Array.isArray(item) ? item : item == null ? [] : [item]))
  }

  if (isPrimitiveValue(value)) return value

  if (typeof value === 'object') {
    if ('value' in value) {
      const nextValue = (value as { value?: unknown }).value
      if (isPrimitiveValue(nextValue)) return nextValue
    }

    if ('id' in value) {
      const nextId = (value as { id?: unknown }).id
      if (isPrimitiveValue(nextId)) return nextId
    }
  }

  return undefined
}

const buildOptionNode = (option: PrimitiveOption): SelectOptionInput => ({
  key: option.id,
  value: option.id,
  disabled: option.disabled,
  label: option.rightAddon ? (
    <span className='flex items-center justify-between gap-2'>
      <span>{option.name}</span>
      {option.rightAddon}
    </span>
  ) : (
    option.name
  ),
  text: option.text ?? option.name,
})

export const buildSelectOptions = (
  options?: FormSelectOptions
): Array<SelectOptionInput | SelectOptionGroupInput> =>
  options?.map((option) =>
    isGroupedOption(option)
      ? {
          key:
            typeof option.label === 'string' || typeof option.label === 'number'
              ? String(option.label)
              : undefined,
          label: option.label,
          options: option.options.map(buildOptionNode),
        }
      : buildOptionNode(option)
  ) ?? []

const flattenOptions = (options: FormSelectOptions): PrimitiveOption[] =>
  options.flatMap((option) => (isGroupedOption(option) ? option.options : [option]))

const getSelectDisplayValue = (
  value: SelectValue | undefined,
  options: PrimitiveOption[]
): string | undefined => {
  const values = Array.isArray(value) ? value : value == null ? [] : [value]
  const labels = values
    .map(getNormalizedSelectValue)
    .flatMap((item) => (Array.isArray(item) ? item : item == null ? [] : [item]))
    .map((item) => {
      const option =
        options.find((x) => x.id === item) ?? options.find((x) => String(x.id) === String(item))

      return option?.name ?? String(item)
    })
    .filter(Boolean)

  return labels.length > 0 ? labels.join(', ') : undefined
}

const getSelectFieldValue = (
  values: Record<string, unknown> | undefined,
  name?: string,
  explicitValue?: SelectValue
): SelectValue | undefined => {
  if (explicitValue !== undefined) {
    return getNormalizedSelectValue(explicitValue)
  }

  if (name?.includes('.')) {
    const rootValue = getNormalizedSelectValue(getNestedValue(values, getValueKey(name)))
    if (rootValue !== undefined) return rootValue
  }

  return getNormalizedSelectValue(getNestedValue(values, name))
}

interface SelectFieldProps extends BaseFieldProps {
  options: FormSelectOptions
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  allowClear?: boolean
  menuItems?: SelectMenuItem[]
  placeholder?: string
  showLoading?: boolean
  showSearch?: boolean
  value?: SelectValue
}

interface FormSelectFieldProps extends SelectFieldProps {
  onChange?: (value: PrimitiveValue) => void
  onValueChange?: (value: PrimitiveValue | undefined) => void
  storeValueAs?: 'option' | 'value'
}

export const FormSelectField: React.FC<FormSelectFieldProps> = (props) => {
  const {
    name,
    options = [],
    disabled,
    readOnly,
    onChange,
    onValueChange,
    onFocus,
    allowClear,
    menuItems,
    placeholder,
    showLoading,
    showSearch,
    storeValueAs = 'option',
    value,
    dataTest,
  } = props

  const formik = useOptionalFormikContext<FormValues>()
  const setFieldValue = useFieldSetter()
  const selectOptions = useMemo(() => buildSelectOptions(options), [options])
  const flatOptions = useMemo(() => flattenOptions(options), [options])
  const resolvedValue = getSelectFieldValue(formik?.values, name, value)
  const displayValue = getSelectDisplayValue(resolvedValue, flatOptions)
  const layoutProps = useCommonFieldLayoutProps(props, displayValue)

  return (
    <FormFieldLayout {...layoutProps}>
      <Select
        value={resolvedValue}
        options={selectOptions}
        readOnly={readOnly}
        disabled={disabled}
        id={name}
        placeholder={placeholder ?? 'Not set'}
        data-test={dataTest}
        allowClear={allowClear}
        menuItems={menuItems}
        showLoading={showLoading}
        showSearch={showSearch}
        onFocus={onFocus}
        onBlur={layoutProps.onBlur}
        onChange={(nextValue) => {
          if (readOnly || disabled) return

          if (onChange) {
            onChange(nextValue as PrimitiveValue)
            return
          }

          if (!formik) return

          if (storeValueAs === 'value') {
            setFieldValue(name, nextValue)
          } else {
            setFieldValue(
              getValueKey(name),
              flatOptions.find((x) => x.id === nextValue)
            )
          }

          onValueChange?.(nextValue)
        }}
      />
    </FormFieldLayout>
  )
}

interface FormReadonlySelectFieldProps extends BaseFieldProps {
  value?: React.ReactNode
  placeholder?: React.ReactNode
  onClick?: React.MouseEventHandler<HTMLButtonElement>
}

export const FormReadonlySelectField: React.FC<FormReadonlySelectFieldProps> = (props) => {
  const {
    value,
    placeholder = 'Not set',
    disabled,
    readOnly = true,
    onClick,
    className,
    dataTest,
  } = props
  const hasValue = hasRenderableValue(value)
  const displayValue = hasValue ? value : placeholder
  const displayText = getNodeText(value)
  const layoutProps = useCommonFieldLayoutProps({ ...props, readOnly }, displayText)
  const isInteractive = Boolean(onClick && !disabled)

  return (
    <FormFieldLayout {...layoutProps} isLinkedValueInteractive={false}>
      <ReadonlySelect items={[]} open={false}>
        <ReadonlySelectTrigger
          type='button'
          aria-readonly={readOnly}
          disabled={disabled}
          data-test={dataTest}
          data-placeholder={hasValue ? undefined : ''}
          className={cn('w-full', isInteractive ? 'cursor-pointer' : 'cursor-default', className)}
          onClick={(event) => {
            if (disabled) return

            event.preventDefault()
            onClick?.(event)
          }}
        >
          <span data-slot='select-value' className='min-w-0 cursor-[inherit] truncate'>
            {displayValue}
          </span>
        </ReadonlySelectTrigger>
      </ReadonlySelect>
    </FormFieldLayout>
  )
}

interface MultiSelectFieldProps extends SelectFieldProps {
  onChange?: (value: string[] | number[] | undefined) => void
  renderSelectionSummary?: MultiSelectProps['renderSelectionSummary']
  selectionSummaryThreshold?: MultiSelectProps['selectionSummaryThreshold']
}

export const FormMultiSelectField: React.FC<MultiSelectFieldProps> = (props) => {
  const {
    name,
    value,
    disabled,
    options = [],
    readOnly,
    onChange,
    allowClear,
    menuItems,
    placeholder,
    showLoading,
    showSearch,
    dataTest,
    renderSelectionSummary,
    selectionSummaryThreshold,
  } = props

  const formik = useOptionalFormikContext<FormValues>()
  const setFieldValue = useFieldSetter()
  const selectOptions = useMemo(() => buildSelectOptions(options), [options])
  const flatOptions = useMemo(() => flattenOptions(options), [options])
  const resolvedValue = getSelectFieldValue(formik?.values, name, value)
  const displayValue = getSelectDisplayValue(resolvedValue, flatOptions)
  const layoutProps = useCommonFieldLayoutProps(props, displayValue)

  return (
    <FormFieldLayout {...layoutProps}>
      <MultiSelect
        disabled={disabled}
        value={resolvedValue}
        options={selectOptions}
        readOnly={readOnly}
        id={name}
        renderSelectionSummary={renderSelectionSummary}
        selectionSummaryThreshold={selectionSummaryThreshold}
        allowClear={allowClear}
        data-test={dataTest}
        menuItems={menuItems}
        placeholder={placeholder}
        showLoading={showLoading}
        showSearch={showSearch}
        className='w-full'
        onBlur={layoutProps.onBlur}
        onChange={(nextValue) => {
          if (readOnly || disabled) return

          if (onChange) {
            onChange(nextValue as string[] | number[] | undefined)
            return
          }

          if (!formik) return

          const mappedValue = Array.isArray(nextValue)
            ? flatOptions.filter((option) => nextValue.includes(option.id))
            : flatOptions.find((option) => option.id === nextValue)

          setFieldValue(getValueKey(name), mappedValue)
        }}
      />
    </FormFieldLayout>
  )
}

interface FormDateFieldProps extends BaseFieldProps {
  onChange?: (value: Date | null) => void
  placeholder?: string
  dateFormatType?: string
  value?: Date | null
  showTimeSelect?: boolean
  isClearable?: boolean
  minDate?: Date
  maxDate?: Date
}

const getDateFieldValue = (fieldValue: unknown, showTimeSelect?: boolean): Date | null => {
  if (!fieldValue) return null

  if (fieldValue instanceof Date) {
    return Number.isNaN(fieldValue.getTime()) ? null : fieldValue
  }

  if (typeof fieldValue === 'string') {
    const parsedValue = new Date(showTimeSelect ? fieldValue : rawDateString(fieldValue))
    if (Number.isNaN(parsedValue.getTime())) return null
    return showTimeSelect ? parsedValue : zonedTimeToUtc(parsedValue)
  }

  return null
}

type DateFieldInputProps = React.ComponentProps<typeof Input> & {
  showClearButton?: boolean
  onClear?: () => void
}

const DateFieldInput = React.forwardRef<HTMLInputElement, DateFieldInputProps>(
  ({ className, disabled, readOnly, showClearButton, onClear, ...props }, ref) => {
    return (
      <InputGroup disabled={disabled} readOnly={readOnly} className='w-full'>
        <InputGroupInput
          ref={ref}
          disabled={disabled}
          readOnly={readOnly}
          className={className}
          {...props}
        />
        {showClearButton && (
          <InputGroupAddon align='inline-end'>
            <Button
              size='icon-xs'
              variant='ghost'
              type='button'
              aria-label='Clear date'
              onMouseDown={(event) => {
                event.preventDefault()
                event.stopPropagation()
              }}
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                onClear?.()
              }}
            >
              <XIcon className='size-4' />
            </Button>
          </InputGroupAddon>
        )}
      </InputGroup>
    )
  }
)

export const FormDateField: React.FC<FormDateFieldProps> = (props) => {
  const {
    readOnly,
    value,
    name,
    disabled,
    dataTest,
    dateFormatType,
    placeholder,
    onChange,
    showTimeSelect,
    isClearable,
    minDate,
    maxDate,
  } = props

  const { setFieldValue, initialValues } = useDateFieldSetter()
  const formik = useOptionalFormikContext<FormValues>()
  const fieldValue = name ? getNestedValue(formik?.values, name) : undefined
  const initialFieldValue = name ? getNestedValue(initialValues, name) : undefined
  const resolvedValue = value ?? getDateFieldValue(fieldValue, showTimeSelect)
  const storesDateObject = initialFieldValue instanceof Date || value instanceof Date
  const layoutProps = useCommonFieldLayoutProps(props, resolvedValue?.toString())
  const showClearButton = Boolean(isClearable && resolvedValue && !readOnly && !disabled)
  const resolvedDateFormat = dateFormatType ?? (showTimeSelect ? 'MM/dd/yyyy h:mm aa' : undefined)

  const handleDateChange = (nextValue: Date | null) => {
    if (readOnly || disabled) return

    if (onChange) {
      onChange(nextValue)
      return
    }

    if (!name || !formik) return

    if (!nextValue) {
      setFieldValue(name, null)
      return
    }

    if (storesDateObject) {
      setFieldValue(name, nextValue)
      return
    }

    setFieldValue(
      name,
      showTimeSelect ? nextValue.toISOString() : rawDateString(nextValue.toISOString())
    )
  }

  return (
    <FormFieldLayout {...layoutProps}>
      <ReactDatePicker
        showYearDropdown
        dropdownMode='select'
        portalId='datepicker-portal'
        readOnly={readOnly}
        disabled={disabled}
        placeholderText={placeholder ?? resolvedDateFormat}
        wrapperClassName='react-date-picker w-full'
        dateFormat={resolvedDateFormat}
        showTimeSelect={showTimeSelect}
        minDate={minDate}
        maxDate={maxDate}
        selected={resolvedValue}
        onChange={handleDateChange}
        onBlur={layoutProps.onBlur}
        customInput={
          <DateFieldInput
            data-test={dataTest}
            id={name}
            showClearButton={showClearButton}
            onClear={() => handleDateChange(null)}
          />
        }
      />
    </FormFieldLayout>
  )
}

interface FormFileFieldProps extends BaseFieldProps {
  onChange?: (value: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  value?: string | number
  accept?: HTMLInputElement['accept']
  multiple?: boolean
}

export interface FormDocumentFileInfo extends UploadFileInfo {
  discontinued: boolean
  inEdit: boolean
}

export const toDocumentFileInfo = (
  file: File,
  batchId: number,
  index: number
): FormDocumentFileInfo => ({
  ...toUploadFileInfo(file, `${batchId}-${index}-${file.name}-${file.size}-${file.lastModified}`),
  discontinued: true,
  inEdit: true,
})

export const FormFileField: React.FC<FormFileFieldProps> = (props) => {
  const { readOnly, accept, value, name, disabled, onChange, multiple } = props

  const setFieldValue = useFieldSetter()
  const resolvedValue =
    useFieldValue<string | number | undefined>({ name, value, fallback: '' }) ?? ''
  const layoutProps = useCommonFieldLayoutProps(props, resolvedValue?.toString())

  return (
    <FormFieldLayout {...layoutProps}>
      <Input
        type='file'
        accept={accept}
        disabled={disabled}
        onChange={(event) => {
          if (readOnly || disabled) return

          if (onChange) {
            onChange(event)
            return
          }

          setFieldValue(name, event.target.value)
        }}
        multiple={multiple}
        className={cn(
          'block h-7 w-full rounded-md border border-input px-0 py-0 text-sm file:mr-3 file:h-6.5 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-0 file:text-sm file:font-medium',
          !disabled && !readOnly && 'cursor-pointer file:cursor-pointer'
        )}
      />
    </FormFieldLayout>
  )
}
